import { Info, Scale } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { Breadcrumb, type BreadcrumbSegment } from '../../../../components/Breadcrumb';
import { ErrorState } from '../../../../components/ErrorState';
import { HistoryLogsModal } from '../../../../components/index-page/HistoryLogsModal';
import { PreviewTable } from '../../../../components/PreviewTable';
import { Skeleton } from '../../../../components/Skeleton';
import { ProfileBanner } from '../../../../components/profile/ProfileBanner';
import { ProfileCard } from '../../../../components/profile/ProfileCard';
import { ProfileHeader } from '../../../../components/profile/ProfileHeader';
import { ProfileHeaderShell } from '../../../../components/profile/ProfileHeaderShell';
import { ApiError } from '../../../../lib/apiError';
import { formatDateTime } from '../../../../lib/formatDate';
import { applicantsApi } from '../applicantsApi';
import { APPLICANT_SCORE_WEIGHTS_AUDIT_ENTITY_TYPES, type ApplicantScoreWeights } from '../applicantTypes';
import { ScoreWeightsModal } from '../components/ScoreWeightsModal';
import { RECRUITMENT_SETTINGS_PATH } from '../recruitmentPaths';

type TabKey = 'info';

const BREADCRUMB: BreadcrumbSegment[] = [
  'Human Resources',
  'Recruitment',
  { label: 'Settings', to: RECRUITMENT_SETTINGS_PATH },
  'Score Points',
];

/**
 * Score Points — the weight (%) each assessment stage carries in an applicant's Summary. One settings
 * row, read here and edited through its own card — Building Management's Aging page, in shape.
 *
 * **Readable on `applicants.read`, editable on `applicants.configure-weights`** — anybody reading a
 * Summary is entitled to know what it is scored against; changing a weight re-scores every applicant at
 * once, so the Edit carries the narrower key and hides without it.
 */
export function ScorePointsSettingsPage() {
  const [weights, setWeights] = useState<ApplicantScoreWeights | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [editing, setEditing] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [tab, setTab] = useState<TabKey>('info');

  const load = useCallback((signal?: AbortSignal) => {
    applicantsApi
      .scoreWeights(signal)
      .then((next) => {
        if (signal?.aborted) return;
        setWeights(next);
        setError(null);
      })
      .catch((caught: unknown) => {
        if (signal?.aborted) return;
        setError(caught instanceof ApiError ? caught : new ApiError('Could not load the score points.', 0));
      });
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);

    return () => controller.abort();
  }, [load]);

  if (error && !weights) {
    return (
      <div className="flex h-full min-h-0 min-w-0 flex-col bg-slate-100">
        <Breadcrumb segments={BREADCRUMB} />
        <ErrorState message={error.message} correlationId={error.correlationId} onRetry={() => load()} />
      </div>
    );
  }

  if (!weights) {
    return (
      <div className="flex h-full min-h-0 min-w-0 flex-col bg-slate-100">
        <Breadcrumb segments={BREADCRUMB} />
        <div className="flex flex-col gap-4 p-4">
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-48 w-full max-w-4xl" />
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col bg-slate-100">
      <Breadcrumb segments={BREADCRUMB} />

      <ProfileHeaderShell<TabKey>
        id="score-points"
        tabs={[{ key: 'info', label: 'Info' }]}
        activeTab={tab}
        onTabChange={setTab}
        panel="cards"
        header={
          <ProfileHeader
            id="score-points-header"
            icon={<Scale className="h-5 w-5" aria-hidden="true" />}
            title="Score Points"
            meta={[`${weights.weights.length} stages`, 'Totalling 100']}
            createdBy={weights.createdByName}
            createdAt={weights.createdAt ?? undefined}
            // History only: the Weights card's cog owns the one Edit — a one-card page must not offer it twice.
            settings={{ onHistory: () => setHistoryOpen(true) }}
          />
        }
        banner={
          <ProfileBanner
            id="score-points-banner"
            tone="slate"
            icon={<Info className="h-4 w-4" aria-hidden="true" />}
            message={
              weights.isConfigured
                ? 'Every applicant’s Summary is scored against these weights: a Passed stage earns its weight, a Failed one nothing.'
                : 'No weights are set yet, so no Summary can be scored. Set a weight for every stage — together they make 100.'
            }
          />
        }
      >
        <ProfileCard
          id="score-points-weights"
          title="Weights"
          onEdit={() => setEditing(true)}
          editPermission="applicants.configure-weights"
          empty={
            weights.isConfigured
              ? undefined
              : {
                  title: 'No weights set.',
                  description: 'Give every assessment stage its share of 100.',
                }
          }
        >
          <PreviewTable
            variant="table-display"
            rows={weights.weights.map((w) => ({
              label: w.stageLabel,
              value: w.weight != null ? `${w.weight.toFixed(2)}%` : null,
              mono: true,
            }))}
          />
        </ProfileCard>

        {/* Never editable: a record of what happened. */}
        <ProfileCard id="score-points-audit" title="Audit">
          <PreviewTable
            variant="table-display"
            rows={[
              { label: 'Created by', value: weights.createdByName },
              { label: 'Created', value: weights.createdAt ? formatDateTime(weights.createdAt) : null, mono: true },
              { label: 'Updated by', value: weights.updatedByName },
              { label: 'Updated', value: weights.updatedAt ? formatDateTime(weights.updatedAt) : null, mono: true },
            ]}
          />
        </ProfileCard>
      </ProfileHeaderShell>

      {editing ? (
        <ScoreWeightsModal
          current={weights}
          onClose={() => setEditing(false)}
          onSaved={(next) => {
            setEditing(false);
            setWeights(next);
          }}
        />
      ) : null}

      <HistoryLogsModal
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        module="applicants"
        entityTypes={APPLICANT_SCORE_WEIGHTS_AUDIT_ENTITY_TYPES}
      />
    </div>
  );
}
